// src/utils/intelligenceExport.js
// Generates a branded DILEX Business Metrics PDF report
// Uses jsPDF — loaded via CDN,

export const generateIntelligenceReport = async (flags, summary, period = 'weekly') => {

  // ── Load jsPDF from CDN if not already loaded ─────────────────────────────
  if (!window.jspdf) {
    await new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/jspdf/2.5.1/jspdf.umd.min.js';
      script.onload  = resolve;
      script.onerror = reject;
      document.head.appendChild(script);
    });
  }

  const { jsPDF } = window.jspdf;
  const doc       = new jsPDF({ orientation: 'portrait', unit: 'mm', format: 'a4' });

  const W         = 210;  // A4 width mm
  const MARGIN    = 18;
  const CONTENT_W = W - MARGIN * 2;
  let   y         = 0;    // current Y cursor

  const now       = new Date();
  const dateStr   = now.toLocaleDateString('en-KE', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' });
  const timeStr   = now.toLocaleTimeString('en-KE', { hour: '2-digit', minute: '2-digit' });

  const periodLabel = period === 'weekly'   ? 'Weekly'
                    : period === 'biweekly' ? 'Bi-Weekly'
                    : 'Monthly';

  // ── Colour palette ─────────────────────────────────────────────────────────
  const BRAND_RED    = [171, 45,  42];
  const BRAND_GOLD   = [212, 175, 55];
  const DARK         = [15,  23,  42];
  const LIGHT_GREY   = [245, 247, 250];
  const MID_GREY     = [100, 116, 139];
  const WHITE        = [255, 255, 255];
  const CRITICAL_RED = [239, 68,  68];
  const HIGH_ORG     = [249, 115, 22];
  const MED_AMB      = [245, 158, 11];
  const POS_GRN      = [34,  197, 94];

  const sevColor = (label) => {
    if (label === 'CRITICAL') return CRITICAL_RED;
    if (label === 'HIGH')     return HIGH_ORG;
    if (label === 'MEDIUM')   return MED_AMB;
    if (label === 'POSITIVE') return POS_GRN;
    return MID_GREY;
  };

  // ── Helper: add new page if needed ────────────────────────────────────────
  const checkPage = (needed = 20) => {
    if (y + needed > 270) {
      doc.addPage();
      y = MARGIN;
      drawPageFooter();
    }
  };

  // ── Helper: draw horizontal rule ──────────────────────────────────────────
  const rule = (color = [220, 220, 230], thickness = 0.3) => {
    doc.setDrawColor(...color);
    doc.setLineWidth(thickness);
    doc.line(MARGIN, y, W - MARGIN, y);
    y += 3;
  };

  // ── Footer on each page ───────────────────────────────────────────────────
  const drawPageFooter = () => {
    const pageNum = doc.internal.getCurrentPageInfo().pageNumber;
    doc.setFontSize(8);
    doc.setTextColor(...MID_GREY);
    doc.text(`DILEX Business Metrics Report — ${periodLabel} — ${dateStr}`, MARGIN, 287);
    doc.text(`Page ${pageNum}`, W - MARGIN, 287, { align: 'right' });
    doc.setDrawColor(...BRAND_GOLD);
    doc.setLineWidth(0.5);
    doc.line(MARGIN, 284, W - MARGIN, 284);
  };

  // ══════════════════════════════════════════════════════════════════════════
  // PAGE 1 — LETTERHEAD + COVER
  // ══════════════════════════════════════════════════════════════════════════

  // ── Gold top bar ──────────────────────────────────────────────────────────
  doc.setFillColor(...BRAND_GOLD);
  doc.rect(0, 0, W, 4, 'F');

  // ── Dark header band ──────────────────────────────────────────────────────
  doc.setFillColor(...DARK);
  doc.rect(0, 4, W, 44, 'F');

  // ── DILEX wordmark ────────────────────────────────────────────────────────
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(32);
  doc.setTextColor(...BRAND_GOLD);
  doc.text('DILEX', MARGIN, 30);

  // ── Tagline ───────────────────────────────────────────────────────────────
  doc.setFontSize(9);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...BRAND_GOLD);
  doc.text('Operations & Field Service Management', MARGIN, 38);

  // ── Report title right side ───────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...WHITE);
  doc.text('BUSINESS METRICS REPORT', W - MARGIN, 26, { align: 'right' });
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...BRAND_GOLD);
  doc.text(`${periodLabel.toUpperCase()} | ${dateStr} | ${timeStr}`, W - MARGIN, 33, { align: 'right' });

  // ── Gold bottom bar of header ─────────────────────────────────────────────
  doc.setFillColor(...BRAND_GOLD);
  doc.rect(0, 48, W, 1.5, 'F');

  y = 58;

  // ── Confidentiality notice ────────────────────────────────────────────────
  doc.setFillColor(255, 248, 220);
  doc.setDrawColor(...BRAND_GOLD);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, y, CONTENT_W, 9, 2, 2, 'FD');
  doc.setFontSize(8);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...[120, 80, 0]);
  doc.text('CONFIDENTIAL — For DILEX Management Use Only. Not for External Distribution.', W / 2, y + 6, { align: 'center' });
  y += 14;

  // ── Report period description ─────────────────────────────────────────────
  doc.setFontSize(10);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MID_GREY);
  const periodDesc = period === 'weekly'   ? 'This report covers the last 7 days of field operations.'
                   : period === 'biweekly' ? 'This report covers the last 14 days of field operations.'
                   : 'This report covers the last 30 days of field operations.';
  doc.text(periodDesc, MARGIN, y);
  y += 5;
  doc.text('All findings are based on live data from the DILEX system. Issues are reported without omission.', MARGIN, y);
  y += 10;

  rule(BRAND_GOLD, 0.8);

  // ── Executive summary box ─────────────────────────────────────────────────
  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('EXECUTIVE SUMMARY', MARGIN, y);
  y += 7;

  // Summary stat boxes
  const boxW   = (CONTENT_W - 9) / 4;
  const boxes  = [
    { label: 'CRITICAL', value: summary.critical, color: CRITICAL_RED },
    { label: 'HIGH',     value: summary.high,     color: HIGH_ORG     },
    { label: 'MEDIUM',   value: summary.medium,   color: MED_AMB      },
    { label: 'POSITIVE', value: summary.positive, color: POS_GRN      }
  ];

  boxes.forEach((box, i) => {
    const bx = MARGIN + i * (boxW + 3);
    // Box background
    doc.setFillColor(box.color[0], box.color[1], box.color[2]);
    doc.setGState(new doc.GState({ opacity: 0.12 }));
    doc.roundedRect(bx, y, boxW, 22, 2, 2, 'F');
    doc.setGState(new doc.GState({ opacity: 1 }));
    // Border
    doc.setDrawColor(...box.color);
    doc.setLineWidth(0.5);
    doc.roundedRect(bx, y, boxW, 22, 2, 2, 'S');
    // Number
    doc.setFontSize(20);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...box.color);
    doc.text(String(box.value), bx + boxW / 2, y + 13, { align: 'center' });
    // Label
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...box.color);
    doc.text(box.label, bx + boxW / 2, y + 19, { align: 'center' });
  });
  y += 28;

  // Overall verdict
  const hasIssues   = summary.critical > 0 || summary.high > 0;
  const verdictColor = summary.critical > 0 ? CRITICAL_RED : summary.high > 0 ? HIGH_ORG : POS_GRN;
  const verdictText  = summary.critical > 0
    ? `${summary.critical} critical issue${summary.critical > 1 ? 's' : ''} require immediate management intervention.`
    : summary.high > 0
    ? `${summary.high} high-priority issue${summary.high > 1 ? 's' : ''} must be addressed this ${period === 'weekly' ? 'week' : 'period'}.`
    : 'No critical or high-priority issues identified. Operations are performing to standard.';

  doc.setFillColor(...verdictColor);
  doc.setGState(new doc.GState({ opacity: 0.1 }));
  doc.roundedRect(MARGIN, y, CONTENT_W, 12, 2, 2, 'F');
  doc.setGState(new doc.GState({ opacity: 1 }));
  doc.setDrawColor(...verdictColor);
  doc.setLineWidth(0.4);
  doc.roundedRect(MARGIN, y, CONTENT_W, 12, 2, 2, 'S');
  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...verdictColor);
  doc.text(`VERDICT: ${verdictText}`, MARGIN + 4, y + 7.5);
  y += 17;

  rule(BRAND_GOLD, 0.8);

  // ══════════════════════════════════════════════════════════════════════════
  // FINDINGS — one card per flag
  // ══════════════════════════════════════════════════════════════════════════

  doc.setFontSize(12);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('DETAILED FINDINGS & RECOMMENDATIONS', MARGIN, y);
  y += 8;

  const risksOnly     = flags.filter(f => f.severity.label !== 'POSITIVE');
  const positivesOnly = flags.filter(f => f.severity.label === 'POSITIVE');

  const drawFlag = (flag, index) => {
    const sc     = sevColor(flag.severity.label);
    const lines1 = doc.splitTextToSize(flag.detail,  CONTENT_W - 8);
    const lines2 = doc.splitTextToSize(flag.action,  CONTENT_W - 8);
    const cardH  = 10 + (lines1.length * 4.5) + (lines2.length * 4.5) + 22;

    checkPage(cardH + 4);

    // Card background
    doc.setFillColor(...LIGHT_GREY);
    doc.roundedRect(MARGIN, y, CONTENT_W, cardH, 2, 2, 'F');

    // Left severity stripe
    doc.setFillColor(...sc);
    doc.rect(MARGIN, y, 3, cardH, 'F');

    // Severity badge
    doc.setFillColor(...sc);
    doc.setGState(new doc.GState({ opacity: 0.15 }));
    doc.roundedRect(MARGIN + 6, y + 4, 22, 6, 1, 1, 'F');
    doc.setGState(new doc.GState({ opacity: 1 }));
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...sc);
    doc.text(flag.severity.label, MARGIN + 17, y + 8.5, { align: 'center' });

    // Category
    doc.setFontSize(7);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(...MID_GREY);
    doc.text(flag.category, MARGIN + 32, y + 8.5);

    // Metric right
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...sc);
    doc.text(flag.metric || '', W - MARGIN - 2, y + 8.5, { align: 'right' });

    // Technician if present
    if (flag.technician) {
      doc.setFontSize(7);
      doc.setFont('helvetica', 'italic');
      doc.setTextColor(...MID_GREY);
      const techX = MARGIN + 32 + doc.getTextWidth(flag.category) + 6;
      doc.text(`· ${flag.technician}`, techX, y + 8.5);
    }

    // Title
    const titleY = y + 16;
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK);
    const titleLines = doc.splitTextToSize(flag.title, CONTENT_W - 8);
    doc.text(titleLines, MARGIN + 6, titleY);
    let innerY = titleY + titleLines.length * 5 + 2;

    // Analysis label + text
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...MID_GREY);
    doc.text('ANALYSIS', MARGIN + 6, innerY);
    innerY += 4;
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    doc.setTextColor(50, 65, 80);
    doc.text(lines1, MARGIN + 6, innerY);
    innerY += lines1.length * 4.5 + 3;

    // Action label + text
    doc.setFontSize(7);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...sc);
    doc.text('RECOMMENDED ACTION', MARGIN + 6, innerY);
    innerY += 4;
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(8.5);
    doc.setTextColor(...DARK);
    doc.text(lines2, MARGIN + 6, innerY);

    y += cardH + 4;
  };

  // ── Risk flags ────────────────────────────────────────────────────────────
  if (risksOnly.length === 0) {
    checkPage(18);
    doc.setFillColor(...POS_GRN);
    doc.setGState(new doc.GState({ opacity: 0.1 }));
    doc.roundedRect(MARGIN, y, CONTENT_W, 14, 2, 2, 'F');
    doc.setGState(new doc.GState({ opacity: 1 }));
    doc.setFontSize(10);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...POS_GRN);
    doc.text('No risk flags identified in this period. Operations are clean.', W / 2, y + 9, { align: 'center' });
    y += 18;
  } else {
    risksOnly.forEach((flag, i) => drawFlag(flag, i));
  }

  // ── Positive flags ────────────────────────────────────────────────────────
  if (positivesOnly.length > 0) {
    checkPage(20);
    rule(BRAND_GOLD, 0.8);
    doc.setFontSize(12);
    doc.setFont('helvetica', 'bold');
    doc.setTextColor(...DARK);
    doc.text('POSITIVE PERFORMANCE', MARGIN, y);
    y += 8;
    positivesOnly.forEach((flag, i) => drawFlag(flag, i));
  }

  // ══════════════════════════════════════════════════════════════════════════
  // CLOSING SECTION
  // ══════════════════════════════════════════════════════════════════════════
  checkPage(50);
  rule(BRAND_GOLD, 0.8);

  doc.setFontSize(9);
  doc.setFont('helvetica', 'bold');
  doc.setTextColor(...DARK);
  doc.text('ABOUT THIS REPORT', MARGIN, y);
  y += 6;
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8.5);
  doc.setTextColor(...MID_GREY);
  const closing = [
    'This report is automatically generated by the DILEX Business Metrics Engine.',
    'All data is sourced in real-time from the DILEX field management system.',
    'Findings are rule-based and applied consistently without exception or favour.',
    'Management is expected to act on critical and high-priority findings within 48 hours.',
    `Report generated: ${dateStr} at ${timeStr}`
  ];
  closing.forEach(line => { doc.text(line, MARGIN, y); y += 5; });

  y += 4;

  // Signature block
  doc.setDrawColor(...BRAND_GOLD);
  doc.setLineWidth(0.5);
  doc.line(MARGIN, y, MARGIN + 60, y);
  doc.line(W / 2, y, W / 2 + 60, y);
  y += 5;
  doc.setFontSize(8);
  doc.setFont('helvetica', 'normal');
  doc.setTextColor(...MID_GREY);
  doc.text('Operations Manager Signature', MARGIN, y);
  doc.text('Admin / Director Signature', W / 2, y);

  // ── Draw footer on every page ─────────────────────────────────────────────
  const totalPages = doc.internal.getNumberOfPages();
  for (let i = 1; i <= totalPages; i++) {
    doc.setPage(i);
    drawPageFooter();
  }

  // ── Save ──────────────────────────────────────────────────────────────────
  const filename = `DILEX_BusinessMetrics_${periodLabel}_${now.toISOString().slice(0, 10)}.pdf`;
  doc.save(filename);
};
